import React from 'react';
import styled from 'styled-components';
import { Breakpoints, Grid } from '../../app/StyleGuide';
import ActionableTitleBar from '../../components/ActionableTitleBar';
import { LinkStyleButton, PrimaryButton, RoundedIconButton } from '../../components/Buttons';
import ErrorMessage from '../../components/ErrorMessage';
import { Container, FixedHeaderContainer, FixedHeaderContent } from '../../components/Layout';
import LoadingState from '../../components/LoadingState';
import { ChurchConnectedItem, ChurchSelectableItem } from './components/ChurchItem';
import { useChurchAccountCreation } from './hooks/useChurchAccountCreation';
import { getAccountText } from './utils/getAccountText';

const ContentContainer = styled(Container)`
  height: 100%;
  max-width: 480px;
  padding: ${Grid._6} ${Grid._4};
  display: flex;
  flex-direction: column;
  gap: ${Grid._4};
  min-height: 0;

  @media screen and (max-width: ${Breakpoints.screen_sm}) {
    padding: ${Grid._5} ${Grid._3};
  }
`;

const ScreenContent = styled(FixedHeaderContent)`
  overflow-y: hidden;
`;

const ChurchList = styled.div`
  flex: 0 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: ${Grid._3};
  overflow-y: auto;

  @media screen and (min-width: ${Breakpoints.screen_sm}) {
    gap: ${Grid._4};
  }
`;

const ActionSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${Grid._3};

  button {
    width: 100%;
    max-width: 280px;
    margin: 0 auto;
  }
`;

export const ChurchAccountCreation = () => {
  const {
    status,
    churchAccounts,
    selectedChurchAccountId,
    onSelectChurchAccount,
    onCreateChurch,
    onCreateChurchAccount,
    onBack,
  } = useChurchAccountCreation();

  return status === 'loading' ? (
    <LoadingState hideText />
  ) : status === 'error' ? (
    <ErrorMessage />
  ) : (
    <FixedHeaderContainer fullViewport>
      <ActionableTitleBar
        title="Select Your Lifeway Church Account"
        subtitle="Choose the church account that should be used to create your Ministry Grid church. Information will sync automatically."
        leftButton={
          <RoundedIconButton data-qa-hook="back" onClick={onBack}>
            <i className="fas fa-arrow-left fa-fw"></i>
            Back
          </RoundedIconButton>
        }
        rightButton={null}
      />
      <ScreenContent data-tracking-section="create-from-church-account">
        <ContentContainer>
          <ChurchList>
            {churchAccounts.map(account =>
              Boolean(account.orgId) ? (
                <ChurchConnectedItem key={account.churchAccountId} name={account.name} meta={getAccountText(account)} />
              ) : (
                <ChurchSelectableItem
                  key={account.churchAccountId}
                  name={account.name}
                  meta={getAccountText(account)}
                  isSelected={selectedChurchAccountId === account.churchAccountId}
                  onSelect={() => onSelectChurchAccount(account)}
                />
              )
            )}
          </ChurchList>

          <ActionSection>
            <PrimaryButton
              disabled={!selectedChurchAccountId || status === 'submitting'}
              onClick={onCreateChurch}
              operating={status === 'submitting'}
              data-tracking-id="create-church-account-confirm"
            >
              Continue
            </PrimaryButton>
            <LinkStyleButton onClick={onCreateChurchAccount} data-tracking-id="create-new-church-account">
              Create New Church Account
            </LinkStyleButton>
          </ActionSection>
        </ContentContainer>
      </ScreenContent>
    </FixedHeaderContainer>
  );
};
